import { useCallback, useEffect, useState } from 'react';
import DashboardLayout from '../../layouts/DashoardLayout';
import { Box, MenuItem, Paper, Select, Stack, Tab, Tabs, TabPanel, Typography } from '../../ui/core';
import { surfaceCardSx as cardSx, cardHeaderSx, cardBodySx } from '../../ui/styles';
import { STATUS } from './claimFormatters';
import useClaimsTab from './useClaimsTab';
import ClaimsTable, { ACTIVE_COLUMNS, COMPLETED_COLUMNS, CANCELED_COLUMNS } from './ClaimsTable';
import ClaimsSearchBar from './ClaimsSearchBar';

// Single-column layout, matching the Rider-Driver Exclusions page's scaffold.
const pageSx = {
  flex: 1,
  minWidth: 0,
  width: '100%',
  boxSizing: 'border-box',
  display: 'flex',
  justifyContent: 'center',
  p: '12px',
  minHeight: '100vh',
  overflowY: 'auto',
};

const TABS = [
  { key: 'active', label: 'Active / In Progress', status: STATUS.ACTIVE, columns: ACTIVE_COLUMNS, emptyText: 'No active claims.' },
  { key: 'completed', label: 'Completed', status: STATUS.COMPLETED, columns: COMPLETED_COLUMNS, emptyText: 'No completed claims.' },
  { key: 'canceled', label: 'Canceled', status: STATUS.CANCELED, columns: CANCELED_COLUMNS, emptyText: 'No canceled claims.' },
];

const DriverQuestsShell = () => {
  const [tab, setTab] = useState(TABS[0].key);
  const [searchQuery, setSearchQuery] = useState('');
  const [questId, setQuestId] = useState('');
  // The union of every questId seen across any tab's loaded rows, labeled by
  // questTitle — there is no "list quest types" endpoint (out of scope), so
  // the filter's options can only grow from what's already been fetched. The
  // set is never pruned, so narrowing to one quest doesn't collapse the
  // dropdown to a single option, and the selected value always stays an
  // option even once it's the only one in view.
  const [questOptions, setQuestOptions] = useState([]);

  // Stable identity: ClaimsSearchBar's debounce effect depends on this, and a
  // fresh function every render would restart the 500ms timer on every
  // unrelated re-render (e.g. a tab's rows landing) instead of only on typing.
  const onSearch = useCallback((value) => setSearchQuery(value), []);

  const active = useClaimsTab(STATUS.ACTIVE, { searchQuery, questId });
  const completed = useClaimsTab(STATUS.COMPLETED, { searchQuery, questId });
  const canceled = useClaimsTab(STATUS.CANCELED, { searchQuery, questId });

  const tabState = { active, completed, canceled };

  useEffect(() => {
    const allRows = [...active.rows, ...completed.rows, ...canceled.rows];
    setQuestOptions((prev) => {
      const known = new Set(prev.map((option) => option.id));
      const additions = [];
      allRows.forEach((row) => {
        if (row.questId && !known.has(row.questId)) {
          known.add(row.questId);
          additions.push({ id: row.questId, title: row.questTitle || row.questId });
        }
      });
      return additions.length ? [...prev, ...additions].sort((a, b) => a.title.localeCompare(b.title)) : prev;
    });
  }, [active.rows, completed.rows, canceled.rows]);

  return (
    <Box sx={pageSx}>
      <Stack spacing="16px" sx={{ maxWidth: 1120, width: '100%' }}>
        <Box>
          <Typography sx={{ fontSize: '22px', fontWeight: 600, letterSpacing: '-0.01em' }}>
            Driver Quests
          </Typography>
          <Typography color="text.secondary" sx={{ fontSize: '13px', mt: '4px' }}>
            Read-only view of driver quest claims and payout status.
          </Typography>
        </Box>

        <Paper elevation={0} sx={cardSx}>
          <Box sx={cardBodySx}>
            <Stack direction="row" spacing="12px" alignItems="center">
              <Box sx={{ flex: 1 }}>
                <ClaimsSearchBar onSearch={onSearch} />
              </Box>
              {/* Labeled "Quest", not "Quest type": the filter narrows to one
                  specific quest (questId), not a category of quests. */}
              <Select
                size="small"
                displayEmpty
                value={questId}
                onChange={(e) => setQuestId(e.target.value)}
                sx={{ minWidth: 220 }}
              >
                <MenuItem value="">All quests</MenuItem>
                {questOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.title}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
          </Box>
        </Paper>

        <Paper elevation={0} sx={cardSx}>
          <Box sx={cardHeaderSx}>
            <Tabs value={tab} onChange={(_, value) => setTab(value)} aria-label="driver quest claim tabs">
              {TABS.map((t) => (
                <Tab key={t.key} value={t.key} label={t.label} id={`driver-quests-tab-${t.key}`} />
              ))}
            </Tabs>
          </Box>
          <Box sx={cardBodySx}>
            {TABS.map((t) => {
              const state = tabState[t.key];
              return (
                <TabPanel key={t.key} value={tab} id={t.key}>
                  {state.error && state.rows.length === 0 ? (
                    <Typography color="error" sx={{ fontSize: '13px' }}>
                      Couldn't load {t.label.toLowerCase()} claims. Refresh to try again.
                    </Typography>
                  ) : (
                    <>
                      {state.error && (
                        <Typography color="error" sx={{ fontSize: '13px', mb: '8px' }}>
                          Couldn't refresh — showing the last loaded results.
                        </Typography>
                      )}
                      {!state.error && state.loadMoreError && (
                        <Typography color="error" sx={{ fontSize: '13px', mb: '8px' }}>
                          Couldn't load more results — try again.
                        </Typography>
                      )}
                      <ClaimsTable
                        columns={t.columns}
                        rows={state.rows}
                        loading={state.loading}
                        hasMore={state.hasMore}
                        loadingMore={state.loadingMore}
                        onLoadMore={state.loadMore}
                        emptyText={t.emptyText}
                      />
                    </>
                  )}
                </TabPanel>
              );
            })}
          </Box>
        </Paper>
      </Stack>
    </Box>
  );
};

export { DriverQuestsShell };

// No flag, no permission gate: open to all admins in v1 (see spec). Unlike
// /rider-driver-exclusions, there's no readiness wait for a GrowthBook flag
// because there's no flag to wait on.
const DriverQuests = () => (
  <DashboardLayout flush title="Driver Quests">
    <DriverQuestsShell />
  </DashboardLayout>
);

export default DriverQuests;
